import {
  PrecipitationMapLayer as PrecipitationLayerConfig,
  PublishedDashboard,
} from "@/lib/api";
import TimeRange from "@/types/time-range";
import { DimensionValueSet } from "@/lib/dimensions";
import {
  dynamicDimensionsContainValues,
  getRelevantDimensionValues,
} from "@/lib/dimensions/helpers";
import BaseWmsLayer from "@/features/shared/widgets/map/layers/base-wms-layer";
import { mapLayerOpacityOverrideToFraction } from "@/features/shared/widgets/map/layer-opacity";
import LegendDimensions from "@/features/shared/widgets/map/legends/legend-dimensions";
import LegendImage from "@/features/shared/widgets/map/legends/legend-image";
import LegendImageLoader from "@/features/shared/widgets/map/legends/legend-image-loader";

import PrecipitationLayerDataLoader, {
  PrecipitationLayerData,
} from "./precipitation-layer-data-loader";

export default class PrecipitationLayer extends BaseWmsLayer {
  private layerDataLoader: PrecipitationLayerDataLoader;
  private legendImageLoader: LegendImageLoader;

  private config;
  private data?: PrecipitationLayerData;

  constructor(config: PrecipitationLayerConfig, selectedDashboard: PublishedDashboard) {
    super(selectedDashboard.id, config.id);
    this.layerDataLoader = new PrecipitationLayerDataLoader(
      selectedDashboard.id,
      config.id,
    );
    this.legendImageLoader = new LegendImageLoader(selectedDashboard.id, config.id);
    this.config = config;
  }

  get id() {
    return this.config.id;
  }

  get opacity() {
    return mapLayerOpacityOverrideToFraction(this.config);
  }

  get tileSize() {
    return 1024;
  }
  get minZoom() {
    return this.config.min_allowed_zoom_level_override ?? 4;
  }
  get maxZoom() {
    return this.config.max_allowed_zoom_level_override ?? 4;
  }

  get extent() {
    return this.data?.observations.extent;
  }
  get dimensions() {
    if (!this.data) return;
    return [...this.data.observations.dimensions, ...this.data.forecast.dimensions];
  }

  get settings() {
    return [];
  }

  async initialize(timeRange: TimeRange) {
    this.clearLoadFailures();
    try {
      this.data = await this.layerDataLoader.loadLayerData(timeRange);
    } catch (error) {
      this.data = undefined;
      throw error;
    }
  }

  getTitle(dimensionValues: DimensionValueSet) {
    return this.isForecast(dimensionValues)
      ? this.config.forecast_title
      : this.config.title;
  }

  isActive(dimensionValues: DimensionValueSet) {
    const dimensions = this.getApplicableDimensions(dimensionValues);
    if (!dimensions) return false;
    if (this.hasLoadError(dimensionValues)) return false;

    return dynamicDimensionsContainValues(dimensions, dimensionValues);
  }

  isForecast(dimensionValues: DimensionValueSet): boolean {
    if (!this.data) return false;
    return dynamicDimensionsContainValues(this.data.forecast.dimensions, dimensionValues);
  }

  getRelevantDimensionValues(values: DimensionValueSet) {
    const dimensions = this.getApplicableDimensions(values);
    return getRelevantDimensionValues(dimensions, values);
  }

  async loadLegendImage(dimensionValues: DimensionValueSet) {
    return this.legendImageLoader.loadLegendImage(
      this.getRelevantDimensionValues(dimensionValues),
    );
  }

  renderLegend(dimensionValues: DimensionValueSet) {
    if (!this.config.show_legend) return undefined;

    const currentDimensionValues = this.getRelevantDimensionValues(dimensionValues);
    return (
      <>
        <LegendDimensions dimensionValues={currentDimensionValues} />
        <LegendImage
          layerId={this.id}
          opacity={this.opacity}
          loadLegendImage={this.loadLegendImage.bind(this)}
          dimensionValues={currentDimensionValues}
        />
      </>
    );
  }

  private getApplicableDimensions(dimensionValues: DimensionValueSet) {
    return this.isForecast(dimensionValues)
      ? this.data?.forecast.dimensions
      : this.data?.observations.dimensions;
  }
}
